import {Widget, WidgetLink} from "@dv5/domain"
import React, {FC, useMemo} from "react"
import {LinkCreationState} from "../model/DragState"
import {widgetLinkService} from "../services/widgetLinkService"
import {WidgetLinkPath} from "./widget-link/WidgetLinkPath"
import './LinkLayer.less'

interface Props {
    widgetLinks: WidgetLink[]
    widgets: Widget[]
    linkCreation: LinkCreationState | null
    mousePosition: { x: number; y: number } | null
    selectedLinkIds?: string[]
    onLinkClick: (linkId: string, ctrlKey: boolean) => void
}

export const LinkLayer: FC<Props> = ({
                                         widgetLinks,
                                         widgets,
                                         linkCreation,
                                         mousePosition,
                                         selectedLinkIds = [],
                                         onLinkClick,
                                     }) => {
    const widgetMap = useMemo(() => {
        const map: Record<string, Widget> = {}
        widgets.forEach(w => {
            map[w.id] = w
        })
        return map
    }, [widgets])

    const linkPaths = useMemo(() => {
        return widgetLinks.map(link => {
            const startWidget = widgetMap[link.startWidgetId]
            const endWidget = widgetMap[link.endWidgetId]

            if (!startWidget || !endWidget) return null

            return widgetLinkService.computeLinkPath(link, startWidget, endWidget)
        }).filter(Boolean)
    }, [widgetLinks, widgetMap])

    const previewPath = useMemo(() => {
        if (!linkCreation || !mousePosition) return null

        const sourceWidget = widgetMap[linkCreation.sourceWidgetId]
        if (!sourceWidget) return null

        return widgetLinkService.computePreviewPath(
            sourceWidget,
            linkCreation.sourcePosition,
            mousePosition
        )
    }, [linkCreation, mousePosition, widgetMap])
    const arrowColors = useMemo(() => {
        const seen = new Set<string>()
        return linkPaths.map(link => (link && link.color) ? link.color.substring(1) : '')
            .filter(color => {
                if (seen.has(color)) {
                    return false
                }
                seen.add(color)
                return true
            })
            .map(color => {
                    return {
                        style: {
                            fill: color.length < 3 ? '#5c7cfa' : ('#' + color),
                        },
                        value: color
                    }
                }
            )
    }, [linkPaths])

    return (
        <svg className="linkLayer">
            <defs>
                {arrowColors.map((color, index) => (
                    <marker
                        id={"arrow" + color.value}
                        key={"arrow" + color.value}
                        viewBox="0 0 10 10"
                        refX="9"
                        refY="5"
                        markerWidth="6"
                        markerHeight="6"
                        orient="auto-start-reverse"
                    >
                        <path d="M 0 0 L 10 5 L 0 10 z" className="arrowHead" style={color.style}/>
                    </marker>
                ))}
            </defs>
            {linkPaths.map(link => (
                <WidgetLinkPath
                    key={'link-path-' + link!.id}
                    linkPathResult={link!}
                    isSelected={selectedLinkIds.includes(link!.id)}
                    onClick={onLinkClick}
                />
            ))}
            {previewPath && (
                <path
                    d={previewPath}
                    className="linkPreview"
                />
            )}
        </svg>
    )
}
